/**
 * Detects whether the current browser supports ES2024 features
 * required by Orqon OS. Uses safe try/catch evaluation so it
 * never crashes older engines — it simply returns false.
 */
export function isModernBrowser(): boolean {
  try {
    // 1. Optional chaining & nullish coalescing (ES2020)
    const obj: Record<string, unknown> = {};
    const _a = (obj as { x?: { y?: number } })?.x?.y ?? 42;

    // 2. globalThis (ES2020)
    if (typeof globalThis === 'undefined') {
      console.warn('[isModernBrowser] globalThis missing');
      return false;
    }

    // 3. Promise.allSettled (ES2020)
    if (typeof Promise.allSettled !== 'function') {
      console.warn('[isModernBrowser] Promise.allSettled missing');
      return false;
    }

    // 4. String.prototype.replaceAll (ES2021)
    if (typeof String.prototype.replaceAll !== 'function') {
      console.warn('[isModernBrowser] String.prototype.replaceAll missing');
      return false;
    }

    // 5. structuredClone (baseline 2022)
    if (typeof structuredClone !== 'function') {
      console.warn('[isModernBrowser] structuredClone missing');
      return false;
    }

    // 6. Array.prototype.at (ES2022)
    if (typeof Array.prototype.at !== 'function') {
      console.warn('[isModernBrowser] Array.prototype.at missing');
      return false;
    }

    // 7. Object.hasOwn (ES2022)
    if (typeof Object.hasOwn !== 'function') {
      console.warn('[isModernBrowser] Object.hasOwn missing');
      return false;
    }

    // 8. CSS :has() support (modern rendering engine — baseline 2023)
    if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
      console.warn('[isModernBrowser] CSS.supports missing');
      return false;
    }
    if (!CSS.supports('selector(:has(*))')) {
      console.warn('[isModernBrowser] CSS :has() selector not supported');
      return false;
    }

    // Suppress unused-variable lint
    void _a;

    return true;
  } catch (e) {
    console.warn('[isModernBrowser] Exception during check:', e);
    return false;
  }
}

/**
 * Returns true when the user agent indicates a mobile device.
 */
export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|webOS|BlackBerry|Opera Mini|IEMobile/i.test(
    navigator.userAgent,
  );
}

/**
 * Cached result — evaluated once at module load.
 * Import this instead of calling isModernBrowser() repeatedly.
 */
export const IS_MODERN_BROWSER = isModernBrowser();
